/**
 * O vocabulário da fila de produção de uniformes (F170, ADR-0038).
 *
 * Listas fechadas e escritas no código: é um cliente só por ora, e mudar uma
 * delas é um deploy, não uma tela de cadastro. As mesmas listas existem no
 * contrato (que só pode depender de `@aeris/domain`) e no CHECK da migração
 * 0098 — o teste de paridade em `apps/api` segura as três juntas.
 *
 * Os valores gravados são códigos sem acento e sem espaço; o rótulo que a
 * planilha da loja usa mora no navegador.
 */
/**
 * As etapas por que um pedido passa, na ordem da planilha da loja.
 *
 * `atendimento` é a conversa com o cliente antes da arte (F228): o pedido nasce
 * nela, e é a atendente quem diz quando ele pode ir para a arte — o dono quer
 * saber quantos pedidos cada atendente tem nessa conversa.
 * `arte` é o antes da impressão — arte, Corel e grade ainda sendo fechados; é o
 * que a planilha acompanhava pelas colunas "Arte drive", "Corel" e "Excel".
 * `pronto` é o fim da produção: o pedido sai da fila quando é DESPACHADO ou
 * RETIRADO pelo fluxo de sempre (F159), que é o que o torna `fulfilled`.
 *
 * Voltar é permitido: reimpressão acontece.
 */
export const UNIFORM_STAGES = [
    'atendimento',
    'arte',
    'iniciar-impressao',
    'imprimindo',
    'impresso',
    'cortando',
    'costurando',
    'embalando',
    'pronto',
];
/**
 * A etapa de todo pedido que ainda não tem linha de etapa (F228).
 *
 * Era `arte` até a F228; a migração 0117 gravou `arte` nos pedidos que já
 * existiam, para nenhum deles voltar ao atendimento.
 */
export const FIRST_UNIFORM_STAGE = 'atendimento';
/**
 * As etapas ANTES da fábrica: a conversa com o cliente e a arte (F228).
 *
 * Sair delas para a fábrica pede a arte aprovada.
 */
export const PRE_FACTORY_STAGES = [
    'atendimento',
    'arte',
];
/**
 * A etapa que exige dizer em qual impressora.
 *
 * É o momento em que a resposta existe — e é a resposta que o pedido repetido
 * vai procurar meses depois ("em qual máquina saiu da outra vez?").
 */
export const STAGE_NEEDING_PRINTER = 'imprimindo';
/**
 * Os tamanhos da grade, na ordem em que a planilha os desenha.
 *
 * `ESP` é o especial (sob medida de verdade). `G1`–`G4` são os grandes além do
 * EXG, e os números pares são o infantil.
 */
export const UNIFORM_SIZES = [
    'PP',
    'P',
    'M',
    'G',
    'GG',
    'XG',
    'EXG',
    'ESP',
    'G1',
    'G2',
    'G3',
    'G4',
    '2',
    '4',
    '6',
    '8',
    '10',
    '12',
    '14',
    '16',
];
/**
 * As peças que a loja faz — SUGESTÕES, não uma lista fechada.
 *
 * A peça é texto livre na grade: a próxima encomenda pode ser uma jaqueta, e
 * recusar a grade por isso seria pior do que aceitar um nome novo.
 */
export const UNIFORM_PIECE_SUGGESTIONS = [
    'Camisa',
    'Regata',
    'Shorts',
    'Shorts com bolso',
    'Longa',
    'Manguito',
    'Sunga',
    'Top',
    'Babylook',
    'Polo',
];
/**
 * Como o pedido sai. Uma LISTA na ficha: a grade marca mais de uma quando o
 * cliente ainda não decidiu — "Uber ou retirada".
 */
export const UNIFORM_LOGISTICS = [
    'correios',
    'van',
    'uber',
    'retirada',
];
/**
 * A marcação de pagamento — **anotação, não dinheiro**.
 *
 * É a coluna "Sinal pago?" da planilha: não, sinal, 100% e cortesia (o "MKT" —
 * pedido de divulgação, valor zero). Nada soma isto: o dinheiro de verdade é o
 * pagamento do pedido. Desde a F173 o sinal é recebido pelo caixa, e a tela só
 * oferece "Não" e "Cortesia" — "Sinal" e "100%" ficam para as anotações
 * gravadas antes dela.
 */
export const UNIFORM_PAYMENT_MARKS = [
    'nao',
    'sinal',
    'total',
    'cortesia',
];
/**
 * As funções de quem trabalha na produção (F171).
 *
 * Não são papéis do núcleo — o dono, o gerente, o vendedor continuam sendo o
 * que são. São o que a pessoa FAZ na 4Play: `atendimento` fala com o cliente e
 * fecha a grade, `arte` desenha, `producao` imprime, corta, costura e embala.
 * Uma pessoa pode ter mais de uma. Quem gerencia a produção
 * (`uniforms.manage`) mexe em tudo sem precisar de função.
 */
export const UNIFORM_FUNCTIONS = ['atendimento', 'arte', 'producao'];
/**
 * O andamento da arte, separado da etapa (F171).
 *
 * A etapa diz onde o pedido está na fábrica; a arte tem a sua própria
 * conversa com o cliente — cria, manda, o cliente pede ajuste, aprova. É a
 * arte `aprovada` que libera o pedido para a impressão.
 */
export const UNIFORM_ART_STATUSES = [
    'aguardando',
    'criando',
    'enviada',
    'ajuste',
    'aprovada',
];
export const FIRST_ART_STATUS = 'aguardando';
/** A arte que libera a saída da etapa `arte`. */
export const APPROVED_ART_STATUS = 'aprovada';
/**
 * As etapas em que o pedido está NA FÁBRICA — entre a arte e o pronto.
 *
 * É a fila de quem tem a função `producao`.
 */
export const PRODUCTION_WORK_STAGES = [
    'iniciar-impressao',
    'imprimindo',
    'impresso',
    'cortando',
    'costurando',
    'embalando',
];
/**
 * O que a linha do tempo do pedido registra além da troca de etapa (F171).
 *
 * A troca de etapa tem a tabela dela desde a F170 (`uniform_stage_event`) e
 * entra na linha do tempo junto destes.
 */
export const UNIFORM_EVENT_KINDS = [
    'ficha',
    'grade',
    'personalizados',
    'arte',
    'imagem-adicionada',
    'imagem-removida',
    'impressoras',
    'repetido',
    'link',
    'ocorrencia',
    'ocorrencia-resolvida',
];
/** Limites de texto, os mesmos do contrato e da migração. */
export const UNIFORM_LIMITS = {
    block: 60,
    piece: 40,
    personName: 80,
    personNumber: 10,
    notes: 2_000,
    eventNote: 200,
    /** Uma grade maior que isto não é um pedido, é um erro de colagem. */
    gradeEntries: 400,
    personalizations: 2_000,
    quantity: 100_000,
    attachmentsPerOrder: 20,
    comment: 2_000,
};
export const isUniformStage = (value) => typeof value === 'string' &&
    UNIFORM_STAGES.includes(value);
export const isUniformSize = (value) => typeof value === 'string' &&
    UNIFORM_SIZES.includes(value);
export const isUniformFunction = (value) => typeof value === 'string' &&
    UNIFORM_FUNCTIONS.includes(value);
export const isUniformArtStatus = (value) => typeof value === 'string' &&
    UNIFORM_ART_STATUSES.includes(value);
//# sourceMappingURL=entities.js.map